import React, { useState, useEffect } from 'react';
import { GraduationCap, Mail, Phone, Check, X, Clock, Trash2, Users, BookOpen, Filter } from 'lucide-react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOPIC_LABELS = {
  startup: 'Starting a Business',
  finance: 'Business Finance',
  marketing: 'Marketing & Branding',
  sales: 'Sales Strategies',
  legal: 'Legal & Taxes',
  ecommerce: 'E-Commerce',
  leadership: 'Leadership',
  planning: 'Business Planning'
};

const EXPERIENCE_LABELS = {
  beginner: 'Beginner',
  intermediate: 'Intermediate',
  advanced: 'Advanced',
  expert: 'Expert'
};

export default function AdminBusinessTeaching() {
  const [signups, setSignups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, student, volunteer

  useEffect(() => { fetchSignups(); }, []);

  const fetchSignups = async () => {
    try {
      const res = await axios.get(`${API}/business-teaching`, { withCredentials: true });
      setSignups(res.data);
    } catch (e) { 
      console.error(e); 
      toast.error('Failed to load signups');
    }
    finally { setLoading(false); }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/business-teaching/${id}`, { status }, { withCredentials: true });
      fetchSignups();
      toast.success(`Status updated to ${status}`);
    } catch (e) { toast.error('Failed to update'); }
  };

  const deleteSignup = async (id) => {
    if (!window.confirm('Delete this signup?')) return;
    try {
      await axios.delete(`${API}/business-teaching/${id}`, { withCredentials: true });
      toast.success('Signup deleted');
      fetchSignups();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const statusColors = {
    new: 'bg-gold/10 text-gold',
    contacted: 'bg-blue-100 text-blue-600',
    enrolled: 'bg-olive/10 text-olive',
    completed: 'bg-purple/10 text-purple',
    declined: 'bg-red-100 text-red-600'
  };

  const filteredSignups = signups.filter(s => {
    if (filter === 'all') return true;
    if (filter === 'student') return s.registration_type !== 'volunteer';
    if (filter === 'volunteer') return s.registration_type === 'volunteer';
    return true;
  });

  const studentCount = signups.filter(s => s.registration_type !== 'volunteer').length;
  const volunteerCount = signups.filter(s => s.registration_type === 'volunteer').length;

  return (
    <div data-testid="admin-business-teaching">
      <div className="mb-8">
        <h1 className="font-serif text-3xl text-charcoal flex items-center gap-3">
          <GraduationCap className="text-burgundy" />
          Business Teaching
        </h1>
        <p className="text-charcoal/60 mt-1">Manage business class signups and volunteer teachers</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 border border-gold/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-burgundy/10 rounded-full flex items-center justify-center">
              <Users size={20} className="text-burgundy" />
            </div>
            <div>
              <p className="text-2xl font-bold text-charcoal">{signups.length}</p>
              <p className="text-xs text-charcoal/60">Total Signups</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gold/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-olive/10 rounded-full flex items-center justify-center">
              <BookOpen size={20} className="text-olive" />
            </div>
            <div>
              <p className="text-2xl font-bold text-charcoal">{studentCount}</p>
              <p className="text-xs text-charcoal/60">Students</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gold/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple/10 rounded-full flex items-center justify-center">
              <GraduationCap size={20} className="text-purple" />
            </div>
            <div>
              <p className="text-2xl font-bold text-charcoal">{volunteerCount}</p>
              <p className="text-xs text-charcoal/60">Volunteer Teachers</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6">
        <Button
          variant={filter === 'all' ? 'default' : 'outline'}
          onClick={() => setFilter('all')}
          className={filter === 'all' ? 'bg-burgundy hover:bg-purple' : ''}
        >
          <Filter size={14} className="mr-1" /> All ({signups.length})
        </Button>
        <Button
          variant={filter === 'student' ? 'default' : 'outline'}
          onClick={() => setFilter('student')}
          className={filter === 'student' ? 'bg-olive hover:bg-olive/80' : ''}
        >
          <BookOpen size={14} className="mr-1" /> Students ({studentCount})
        </Button>
        <Button
          variant={filter === 'volunteer' ? 'default' : 'outline'}
          onClick={() => setFilter('volunteer')}
          className={filter === 'volunteer' ? 'bg-purple hover:bg-purple/80' : ''}
        >
          <GraduationCap size={14} className="mr-1" /> Teachers ({volunteerCount})
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      ) : filteredSignups.length > 0 ? (
        <div className="space-y-4">
          {filteredSignups.map((signup) => (
            <div 
              key={signup.id} 
              className={`bg-white rounded-2xl p-6 border ${
                signup.registration_type === 'volunteer' 
                  ? 'border-purple/30 bg-purple/5' 
                  : 'border-gold/10'
              }`}
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-serif text-lg text-charcoal">{signup.name}</h3>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      signup.registration_type === 'volunteer' 
                        ? 'bg-purple/20 text-purple font-medium' 
                        : 'bg-olive/20 text-olive'
                    }`}>
                      {signup.registration_type === 'volunteer' ? '👨‍🏫 Teacher' : '📚 Student'}
                    </span>
                    <span className={`px-2 py-1 text-xs rounded-full ${statusColors[signup.status] || statusColors.new}`}>
                      {signup.status || 'new'}
                    </span>
                  </div>
                  
                  <div className="flex flex-wrap gap-4 text-sm text-charcoal/60">
                    <span className="flex items-center gap-1"><Mail size={14} /> {signup.email}</span>
                    {signup.phone && <span className="flex items-center gap-1"><Phone size={14} /> {signup.phone}</span>}
                    {signup.experience_level && (
                      <span className="text-charcoal/50">
                        Level: {EXPERIENCE_LABELS[signup.experience_level] || signup.experience_level}
                      </span>
                    )}
                  </div>
                  
                  {/* Topics */}
                  {signup.topics?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      <span className="text-xs text-charcoal/50 mr-1">
                        {signup.registration_type === 'volunteer' ? 'Can teach:' : 'Interested in:'}
                      </span>
                      {signup.topics.map((topic, idx) => (
                        <span key={idx} className="px-2 py-1 bg-cream text-xs text-charcoal/70 rounded">
                          {TOPIC_LABELS[topic] || topic}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  {/* Business/Goals */}
                  {signup.current_business && (
                    <p className="text-sm text-charcoal/60 mt-2">
                      <strong>{signup.registration_type === 'volunteer' ? 'Experience:' : 'Business:'}</strong> {signup.current_business}
                    </p>
                  )}
                  
                  {/* Volunteer specific fields */}
                  {signup.registration_type === 'volunteer' && (
                    <>
                      {signup.teaching_experience && (
                        <p className="text-sm text-charcoal/60 mt-1">
                          <strong>Teaching Experience:</strong> {signup.teaching_experience}
                        </p>
                      )}
                      {signup.availability && (
                        <p className="text-sm text-charcoal/60 mt-1">
                          <strong>Availability:</strong> {signup.availability}
                        </p>
                      )}
                      {signup.why_teach && (
                        <p className="text-sm text-charcoal/60 mt-1 italic">
                          <strong>Motivation:</strong> "{signup.why_teach}"
                        </p>
                      )}
                    </>
                  )}
                  
                  {/* Student goals */}
                  {signup.registration_type !== 'volunteer' && signup.goals && (
                    <p className="text-sm text-charcoal/60 mt-2 italic">
                      <strong>Goals:</strong> "{signup.goals}"
                    </p>
                  )}
                  
                  <p className="text-xs text-charcoal/40 mt-3">
                    Signed up: {new Date(signup.created_at).toLocaleDateString()}
                  </p>
                </div>
                
                <div className="flex flex-col gap-2">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => updateStatus(signup.id, 'contacted')} 
                    className="text-blue-600 border-blue-200"
                  >
                    <Clock size={14} className="mr-1" /> Contacted
                  </Button>
                  <Button 
                    size="sm" 
                    onClick={() => updateStatus(signup.id, 'enrolled')} 
                    className="bg-olive text-white hover:bg-olive/80"
                  >
                    <Check size={14} className="mr-1" /> Enroll
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => updateStatus(signup.id, 'completed')} 
                    className="text-purple border-purple/30"
                  >
                    <GraduationCap size={14} className="mr-1" /> Completed
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => deleteSignup(signup.id)} 
                    className="text-red-600 border-red-200"
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-white rounded-2xl border border-gold/10">
          <GraduationCap size={48} className="mx-auto text-charcoal/20 mb-4" />
          <p className="text-charcoal/50">
            {filter === 'volunteer' 
              ? 'No volunteer teachers yet' 
              : filter === 'student' 
                ? 'No student signups yet' 
                : 'No business teaching signups yet'
            }
          </p>
        </div>
      )}
    </div>
  );
}
